import React, { useEffect, useState } from "react";
import styles from "./school-terms.module.css";
import { Typography } from "antd";
import CustomButton from "../../../../../../../common/custom-button/custom-button";
import { PlusOutlined } from "@ant-design/icons";
import CustomTable from "../../../../../../../common/custom-table/custom-table";
import dayjs from "dayjs";
import SVGIcon from "../../../../../../../constants/SVGIcon";
import { deleteTerms, fetchTerms } from "../../../../../../../services/school_info";
import AddEditTerms from "./components/add-edit-terms/add-edit-terms";
import CustomPopover from "../../../../../../../common/custom-popover/custom-popover";
import { toast } from "react-toastify";
import { DATE_FORMAT } from "../../../../../../../constants/common";
import { refetchSchool } from "../../../../../../../utils/common";
import { useAcademicYears } from "../../../../../../../hooks/useAcademicYear";

const SchoolTerms = () => {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editDetails, setEditDetails] = useState(null);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const { selectedYear } = useAcademicYears();

  useEffect(() => {
    if(selectedYear) {
      getTerms();
    }
  }, [selectedYear]);

  const getTerms = async (tempPage = 1, tempPageSize = pageSize) => {
    try {
      setLoading(true);
      const payload = {
        page: tempPage,
        pageSize: tempPageSize,
        academic_year_id: selectedYear,
      };
      const response = await fetchTerms(payload);
      if (response?.data) {
        const data = response?.data?.data?.terms;
        setTerms(data);
        setTotalCount(response?.data?.data?.totalRecords);
      }
      setLoading(false);
    } catch (err) {
      if (err?.data?.message) {
        toast.error(err?.data?.message);
      }
      console.log(err);
      setLoading(false);
    }
  };

  const handleChangePage = (page, pageSize) => {
    setPageNo(page);
    setPageSize(pageSize);
    getTerms(page, pageSize);
  };

  const handleEditTerm = (data) => {
    setEditDetails(data);
    setOpenDrawer(true);
  };

  const handleOnClose = () => {
    getTerms();
    setOpenDrawer(false);
  };

  const handleDeleteTerm = async (id) => {
    try {
      await deleteTerms(id);
      getTerms();
      refetchSchool();
    } catch (err) {
      console.log(err);
      toast.error(err?.data?.message || "Failed to delete term");
    }
  };

  const handleAddTerms = () => {
    setEditDetails(null);
    setOpenDrawer(true);
  };

  const columns = [
    {
      title: "Term Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a?.name?.localeCompare(b?.name),
      render: (value) => value || "-",
    },
    {
      title: "Start date",
      dataIndex: "start_date",
      key: "start_date",
      sorter: (a, b) => dayjs(a?.start_date, DATE_FORMAT.DD_MM_YYYY).valueOf() - dayjs(b?.start_date, DATE_FORMAT.DD_MM_YYYY).valueOf(),
      render: (value) => value || "-",
    },
    {
      title: "End date",
      dataIndex: "end_date",
      key: "end_date",
      sorter: (a, b) => dayjs(a?.end_date, DATE_FORMAT.DD_MM_YYYY).valueOf() - dayjs(b?.end_date, DATE_FORMAT.DD_MM_YYYY).valueOf(),
      render: (value) => value || "-",
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      sorter: (a, b) => b?.is_active - a?.is_active, // true < false (Active before Inactive)
      render: (value) => (
        <div className={`${styles.statusTag} ${value ? styles.activeTag : styles.inactiveTag}`}>
          <span className={`${styles.statusDot} ${value ? styles.activeDot : styles.inactiveDot}`}/>
          <span>{value ? "Active" : "Inactive"}</span>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => {
        const today = dayjs();
        const startDate = dayjs(record?.start_date, DATE_FORMAT.DD_MM_YYYY);
        const endDate = dayjs(record?.end_date, DATE_FORMAT.DD_MM_YYYY);

        const isFutureDate = startDate.isAfter(today);
        const isPastDate = endDate.isBefore(today);

        return (
          <div className={styles.actionItemWrapper}>
            {isPastDate ? (
              <SVGIcon
                name={"edit-icon"}
                width={"20"}
                height={"20"}
                stroke={"rgba(0, 0, 0, 0.25)"}
                style={{ cursor: "not-allowed" }}
              />
            ) : (
              <SVGIcon
                name={"edit-icon"}
                width={"20"}
                height={"20"}
                stroke={"#3BB44B"}
                onClick={() => handleEditTerm(record)}
              />
            )}
            {isFutureDate ? (
              <CustomPopover
                message="Are you sure you want to delete this term?"
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={() => handleDeleteTerm(record?.id)}
              >
                <SVGIcon
                  name={"delete"}
                  width={"20"}
                  height={"20"}
                  stroke={"rgba(243, 17, 17, 1)"}
                />
              </CustomPopover>
            ) : (
              <SVGIcon
                name={"delete"}
                width={"20"}
                height={"20"}
                stroke="rgba(0, 0, 0, 0.25)"
                style={{ cursor: "not-allowed" }}
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div className={styles.titleContainer}>
        <Typography className={styles.tabTitle}>Terms</Typography>
        <div className={styles.action_container}>
          <CustomButton
            title="Add New Term"
            size="large"
            icon={<PlusOutlined />}
            onClick={handleAddTerms}
          />
        </div>
      </div>

      <div className={styles.tableContainer}>
        <CustomTable
          columns={columns}
          dataSource={terms}
          loading={loading}
          headerColor="#667085"
          headerBackgroundColor="#F9FAFB"
          total={totalCount}
          pageSize={pageSize}
          currentPage={pageNo}
          handlePageChange={handleChangePage}
        />
      </div>
      <AddEditTerms
        open={openDrawer}
        onClose={handleOnClose}
        data={editDetails}
      />
    </div>
  );
};

export default SchoolTerms;